import React, { useState, useEffect } from 'react';
import { useParams, useSearch, useNavigate } from '@tanstack/react-router';
import { Button } from '../../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../../components/ui/card';
import QuizParticipant from '../../components/student/QuizParticipant';
import { api, useQuery, useMutation } from '../../lib/convex';
import type { Id } from '../../lib/convex';

const QuizParticipationPage: React.FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams({ from: '/quiz/$sessionId' });
  const searchParams = useSearch({ from: '/quiz/$sessionId' });
  
  const [selectedAnswer, setSelectedAnswer] = useState<number | undefined>(undefined);
  const [showAnswer, setShowAnswer] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [answerStartTime, setAnswerStartTime] = useState(Date.now());
  const [recoveredParticipantId, setRecoveredParticipantId] = useState<string | null>(null);
  const [recoveredNickname, setRecoveredNickname] = useState<string | null>(null);

  // Try to recover from localStorage if not in search params
  useEffect(() => {
    if (!searchParams.participantId || !searchParams.nickname) {
      const stored = localStorage.getItem(`session_${sessionId}`);
      if (stored) {
        try {
          const data = JSON.parse(stored);
          setRecoveredParticipantId(data.participantId);
          setRecoveredNickname(data.nickname);
        } catch (e) {
          console.error('Failed to parse stored session data:', e);
        }
      }
    }
  }, [sessionId, searchParams.participantId, searchParams.nickname]);

  const participantId = searchParams.participantId || recoveredParticipantId || undefined;
  const nickname = searchParams.nickname || recoveredNickname || undefined;
  
  const session = useQuery(api.sessions.get, {
    id: sessionId as Id<"sessions">
  });

  const currentQuestion = useQuery(api.sessions.getCurrentQuestion, {
    id: sessionId as Id<"sessions">
  });

  const participant = useQuery(api.participants.get, 
    participantId ? { id: participantId as Id<"participants"> } : "skip"
  );

  const existingAnswer = useQuery(api.answers.getParticipantQuestionAnswer,
    participantId && session?.currentQuestionIndex !== undefined
      ? {
          sessionId: sessionId as Id<"sessions">,
          participantId: participantId as Id<"participants">,
          questionIndex: session.currentQuestionIndex,
        }
      : "skip"
  );

  const submitAnswer = useMutation(api.answers.submit);

  useEffect(() => {
    setSelectedAnswer(undefined);
    setShowAnswer(false);
    setIsWaiting(false);
    setAnswerStartTime(Date.now());
  }, [session?.currentQuestionIndex]);

  useEffect(() => {
    if (existingAnswer) {
      setSelectedAnswer(existingAnswer.selectedOptionIndex);
      setShowAnswer(true);
    }
  }, [existingAnswer]);

  useEffect(() => {
    if (session?.showingAnswers) {
      setIsWaiting(true);
      setShowAnswer(true);
    }
  }, [session?.showingAnswers]);

  useEffect(() => {
    if (session?.status === 'completed') {
      navigate({ 
        to: '/quiz/$sessionId/results', 
        params: { sessionId },
        search: { participantId }
      });
    }
  }, [session?.status, sessionId, participantId, navigate]);

  if (session?.status === 'completed') {
    navigate({ 
      to: '/quiz/$sessionId/results', 
      params: { sessionId },
      search: { participantId }
    });
    return null;
  }

  const handleSelectAnswer = async (optionIndex: number) => {
    if (selectedAnswer !== undefined) return;
    
    setSelectedAnswer(optionIndex);
    
    try {
      const timeToAnswer = Date.now() - answerStartTime;
      
      await submitAnswer({
        sessionId: sessionId as Id<"sessions">,
        participantId: participantId as Id<"participants">,
        selectedOptionIndex: optionIndex,
        timeToAnswer,
      });
      
      setShowAnswer(true);

      setTimeout(() => {
        setIsWaiting(true);
      }, 3000);
    } catch (error) {
      console.error('Error submitting answer:', error);
      alert('Failed to submit answer. Please try again.');
      setSelectedAnswer(undefined);
    }
  };

  if (!session) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (session.status === 'waiting') {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-center">
              Welcome, {nickname}!
            </CardTitle>
          </CardHeader>
          
          <CardContent className="text-center py-12">
            <div className="mb-6">
              <div className="text-5xl mb-4">⏳</div>
              <p className="text-lg font-medium">
                Waiting for quiz to start...
              </p>
            </div>
            
            <p className="text-muted-foreground">
              Your teacher will start the quiz when ready
            </p>
          </CardContent>
          
          <CardFooter className="justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate({ to: '/' })}
            >
              Leave
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  if (!currentQuestion) {
    return (
      <div className="container max-w-md mx-auto px-4 py-8">
        <div className="text-center py-12">
          <p className="text-muted-foreground">Loading question...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container max-w-md mx-auto px-4 py-8">
      <div className="mb-6 text-center">
        <h1 className="text-xl font-semibold">
          Playing as <span className="text-primary font-bold">{nickname}</span>
        </h1>
      </div>
      
      <QuizParticipant
        currentQuestion={currentQuestion}
        isWaiting={isWaiting}
        showAnswer={showAnswer}
        selectedAnswer={selectedAnswer}
        score={participant?.score ?? 0}
        onSelectAnswer={handleSelectAnswer}
        questionStartTime={session?.questionStartTime}
      />
      
      <div className="mt-8 text-center">
        <Button 
          variant="outline" 
          onClick={() => navigate({ to: '/' })}
        >
          Leave Quiz
        </Button>
      </div>
    </div>
  );
};

export default QuizParticipationPage;